"use client";

import React, { useState, useRef } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { TrainingCard } from "../../Common/TrainingCard";
import { FinancingCard } from "./FinancingCard";
import Button from "@/components/ui/Button";
import { useGetFinancings } from "@/actions/financing";
import { useGetTopFormations } from "@/actions/formation";
import { toBookmark } from "@/lib/bookmarks";

/* Tabs configuration */
const TABS = [
    { id: 0, label: "Les mieux notées" },
    { id: 1, label: "Les plus demandées" },
    { id: 2, label: "Les Financements" },
];

const OurTopTrainingCourses = () => {
    const Router = useRouter();
    const [activeTab, setActiveTab] = useState(0);
    const swiperRef = useRef<SwiperType>(null);
    const { financings, financingsLoading } = useGetFinancings();

    // Both training tabs share the same endpoint, only the ranking differs;
    // the financing tab (2) needs no formations, so the fetch is skipped.
    const { topFormations, topFormationsLoading } = useGetTopFormations(
        activeTab === 0 ? "top-rated" : activeTab === 1 ? "most-requested" : null,
    );

    const swiperSettings = {
        modules: [Navigation, Pagination],
        spaceBetween: 24,
        slidesPerView: 1.1,
        pagination: { clickable: true },
        breakpoints: {
            768: { slidesPerView: 2.2, spaceBetween: 24, pagination: { enabled: true } },
            1024: { slidesPerView: 3, spaceBetween: 32, pagination: { enabled: false } },
            1280: { slidesPerView: 4, spaceBetween: 32, pagination: { enabled: false } },
        },
        navigation: {
            prevEl: '.top-next',
            nextEl: '.top-prev',
        },
        onBeforeInit: (swiper: SwiperType) => {
            swiperRef.current = swiper;
        },
    };

    const financingSwiperSettings = {
        ...swiperSettings,
        slidesPerView: 5,
        pagination: { clickable: true },
        breakpoints: {
            0: { slidesPerView: 1.5, spaceBetween: 16, pagination: { enabled: true } },
            640: { slidesPerView: 2.2, spaceBetween: 20, pagination: { enabled: true } },
            768: { slidesPerView: 2.2, spaceBetween: 24, pagination: { enabled: true } },
            1022: { slidesPerView: 5, spaceBetween: 12, pagination: { enabled: false } },
            1280: { slidesPerView: 5, spaceBetween: 24, pagination: { enabled: false } },
        },
    };

    const renderContent = () => {
        if (activeTab === 0 || activeTab === 1) {
            return (
                <div className="relative">
                    <div className="text-center mb-8 sm:mb-[56px] sm:px-4 max-w-[1140px] mx-auto">
                        <p className="text-[#1D1D1D] text-[14px] md:text-[20px] leading-5 sm:leading-[30px] font-medium">
                            Notées par ceux qui les ont suivies. Chaque formation est évaluée sur des critères précis : <br /> qualité de l&apos;enseignement, pertinence du contenu, impact réel sur vos projets.
                            Notre sélection, c&apos;est votre garantie.{" "}
                            <a href="/methode" className="text-[#1B365E] font-bold">
                                En savoir plus sur notre Méthode
                            </a>
                        </p>
                    </div>

                    <div className="relative max-w-[1306px] mx-auto group">
                        <Swiper key={`training-${activeTab}`} {...swiperSettings} className="w-full pb-10! top-training-swiper">
                            {topFormationsLoading
                                ? Array.from({ length: 5 }).map((_, i) => (
                                    <SwiperSlide key={i} className="h-auto">
                                        <div className="w-full h-full min-h-75.75 bg-[#C7F8FF] rounded-3xl animate-pulse" />
                                    </SwiperSlide>
                                ))
                                : topFormations.map((course, index) => (
                                    <SwiperSlide key={course.id} className="h-auto">
                                        <TrainingCard
                                            rank={index + 1}
                                            rating={course.rating_avg ?? "0"}
                                            title={course.title}
                                            description={course.description}
                                            category={course.category}
                                            location={course.location}
                                            duration={course.duration}
                                            price={course.price}
                                            financing={course.financing ?? ""}
                                            isNew={course.isNew}
                                            formation={toBookmark(course)}
                                            onDiscover={() => Router.push(`/fiche/${course.slug}`)}
                                            onSubscribe={() => Router.push(`/fiche/${course.slug}`)}
                                        />
                                    </SwiperSlide>
                                ))}
                        </Swiper>

                        {/* Navigation Arrows - Using currentColor for dynamic logic */}
                        <div className="hidden xl:flex flex-col gap-5 absolute -right-10 top-3 z-20">
                            <button className="top-next cursor-pointer transition-all hover:scale-110">
                                <svg width="28" height="30" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M27.4142 16.1408C28.1953 15.3597 28.1953 14.0934 27.4142 13.3123L14.6863 0.584427C13.9052 -0.196622 12.6389 -0.196622 11.8579 0.584427C11.0768 1.36548 11.0768 2.63181 11.8579 3.41285L23.1716 14.7266L11.8579 26.0403C11.0768 26.8213 11.0768 28.0876 11.8579 28.8687C12.6389 29.6497 13.9052 29.6497 14.6863 28.8687L27.4142 16.1408ZM0 16.7266H26V12.7266H0V16.7266Z" fill="currentColor" />
                                </svg>
                            </button>
                            <button className="top-prev cursor-pointer transition-all hover:scale-110">
                                <svg width="28" height="30" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg" className="rotate-180">
                                    <path d="M27.4142 16.1408C28.1953 15.3597 28.1953 14.0934 27.4142 13.3123L14.6863 0.584427C13.9052 -0.196622 12.6389 -0.196622 11.8579 0.584427C11.0768 1.36548 11.0768 2.63181 11.8579 3.41285L23.1716 14.7266L11.8579 26.0403C11.0768 26.8213 11.0768 28.0876 11.8579 28.8687C12.6389 29.6497 13.9052 29.6497 14.6863 28.8687L27.4142 16.1408ZM0 16.7266H26V12.7266H0V16.7266Z" fill="currentColor" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Logo Section */}
                    <div className="mt-8 sm:mt-[19px] text-center">
                        <p className="text-[#1B365E] text-[14px] leading-5 font-normal mb-[19px] px-4 sm:block hidden">
                            OPCO, OPCA, CPF, aides régionales... Optimisez le <span className="underline cursor-pointer">financement</span> de votre formation
                        </p>
                        <div className="flex flex-wrap justify-center sm:flex-row flex-col items-center gap-8 sm:gap-24 px-4">
                            <Image src="/assets/icons/opco.png" alt="OPCO" width={160} height={68.8} className="object-contain h-[45px] sm:h-[68.8px]" />
                            <Image src="/assets/icons/mon-compte-formation.png" alt="CPF" width={104} height={69} className="object-contain" />
                            <Image src="/assets/icons/region.svg" alt="Région" width={144} height={34} className="object-contain" />
                        </div>
                    </div>

                    <div className="mt-8 sm:mt-[38px] flex flex-col sm:flex-row justify-center items-center gap-8 w-full">
                        <Button variant="outline-gradient" className="w-full sm:w-fit h-14.5 px-4! sm:px-10!">
                            En savoir plus sur les financements
                        </Button>
                        <Button variant="gradient" onClick={()=>{Router.push('/formations')}} className="sm:w-fit w-full h-14.5 px-10">
                            Découvrir toutes nos formations
                        </Button>
                    </div>
                </div>
            );
        }

        if (activeTab === 2) {
            return (
                <div className="relative">
                    <div className="text-center mb-8 md:mb-[75px] px-4 max-w-[1050px] mx-auto">
                        <p className="text-[#1D1D1D] text-[14px] md:text-[20px] leading-5 sm:leading-[30px] font-medium">
                            Prise en charge de votre formation grâce aux dispositifs d’aide au financement d’une formation professionnelle,
                            adaptés à différentes situations. Optimisez le financement de votre formation selon votre contexte.
                        </p>
                    </div>

                    <div className="relative max-w-[1306px] mx-auto">
                        <Swiper key={`financing-${activeTab}`} {...financingSwiperSettings} className="w-full pb-10">
                            {financingsLoading
                                ? Array.from({ length: 5 }).map((_, i) => (
                                    <SwiperSlide key={i} className="h-auto !pb-8 text-left">
                                        <div className="bg-white rounded-lg border border-[#ABABAB] w-full h-full min-h-[320px] animate-pulse" />
                                    </SwiperSlide>
                                ))
                                : financings.map((option) => (
                                    <SwiperSlide key={option.id} className="h-auto !pb-8 text-left">
                                        <FinancingCard
                                            title={option.title}
                                            subtitle={option.subtitle}
                                            logo={option.logo}
                                            description={option.description}
                                            buttonText={option.buttonText}
                                            buttonUrl={option.buttonUrl}
                                        />
                                    </SwiperSlide>
                                ))}
                        </Swiper>

                        {/* Navigation Arrows for Financing Tab */}
                        <div className="hidden xl:flex flex-col gap-5 absolute -right-14 top-5 z-20">
                            <button className="top-next cursor-pointer transition-all hover:scale-110">
                                <svg width="28" height="30" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M27.4142 16.1408C28.1953 15.3597 28.1953 14.0934 27.4142 13.3123L14.6863 0.584427C13.9052 -0.196622 12.6389 -0.196622 11.8579 0.584427C11.0768 1.36548 11.0768 2.63181 11.8579 3.41285L23.1716 14.7266L11.8579 26.0403C11.0768 26.8213 11.0768 28.0876 11.8579 28.8687C12.6389 29.6497 13.9052 29.6497 14.6863 28.8687L27.4142 16.1408ZM0 16.7266H26V12.7266H0V16.7266Z" fill="currentColor" />
                                </svg>
                            </button>
                            <button className="top-prev cursor-pointer transition-all hover:scale-110">
                                <svg width="28" height="30" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg" className="rotate-180">
                                    <path d="M27.4142 16.1408C28.1953 15.3597 28.1953 14.0934 27.4142 13.3123L14.6863 0.584427C13.9052 -0.196622 12.6389 -0.196622 11.8579 0.584427C11.0768 1.36548 11.0768 2.63181 11.8579 3.41285L23.1716 14.7266L11.8579 26.0403C11.0768 26.8213 11.0768 28.0876 11.8579 28.8687C12.6389 29.6497 13.9052 29.6497 14.6863 28.8687L27.4142 16.1408ZM0 16.7266H26V12.7266H0V16.7266Z" fill="currentColor" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <p className="text-center mt-[23px] text-[#1D1D1D] text-[14px] leading-5 font-normal">Vous pouvez financer tout ou une partie de votre parcours de formation grâce aux dispositifs d’aide.</p>

                    <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row justify-center items-center gap-8 w-full">
                        <Button variant="outline-gradient" className="w-full sm:w-fit h-14.5 px-4! sm:px-10!">
                            En savoir plus sur les financements
                        </Button>
                    </div>
                </div>
            );
        }
    };

    return (
        <section className="sm:mb-5 pt-[34px] md:pt-[60px] overflow-hidden px-5">
            <div className="max-w-[1306px] mx-auto w-full">
                <h2 className="text-center text-[30px] md:text-[40px] leading-10 md:leading-[53px] font-light font-red-hat mb-[20.73px] sm:mb-10 text-[#1B365E]">
                    Le top de nos formations
                </h2>

                <div className="max-w-[350px] md:max-w-[650px] flex flex-wrap items-center justify-between gap-4 mb-9 md:mb-12 mx-auto">
                    {TABS.map((tab) => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`
                                    px-4 md:px-6 h-[26px] md:h-[46px] cursor-pointer rounded-full text-[8px] sm:text-[12px] leading-[12px] sm:leading-3.5 font-bold transition-all border
                                    ${isActive
                                        ? "bg-[#1B365E] text-white border-[#1B365E] shadow-sm"
                                        : "bg-white text-[#1B365E] border-[#EFEFEF] hover:bg-gray-50"}
                                `}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                <div className="min-h-[500px]">
                    {renderContent()}
                </div>

            </div>

            <style jsx>{`
                :global(.top-next),
                :global(.top-prev) {
                    color: #1B365E !important;
                    transition: color 0.3s;
                }
                :global(.top-next.swiper-button-disabled),
                :global(.top-prev.swiper-button-disabled) {
                    color: #ABABAB !important;
                    opacity: 1 !important;
                    cursor: not-allowed !important;
                }
                :global(.swiper) {
                    padding-right: 0 !important;
                }
                /* Swiper's own .swiper-slide{height:100%} is unlayered, so it beats
                   Tailwind's h-auto utility and blocks flex stretching. Forcing auto
                   here lets every TrainingCard match the tallest slide in the row. */
                :global(.top-training-swiper .swiper-wrapper) {
                    align-items: stretch !important;
                }
                :global(.top-training-swiper .swiper-slide) {
                    height: auto !important;
                    display: flex !important;
                    flex-direction: column !important;
                }
            `}</style>
        </section>
    );
};

export default OurTopTrainingCourses;